import { Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { ConfirmProvider } from "./context/ConfirmContext";
import useAuth from "./hooks/useAuth";
import useIsMobile from "./hooks/useIsMobile";
import LoginPage from "./pages/LoginPage";
import ForceChangePasswordPage from "./pages/ForceChangePasswordPage";
import DashboardPage from "./pages/DashboardPage";
import ManageAccountsPage from "./pages/ManageAccountsPage";
import JoRegistryPage from "./pages/JoRegistryPage";
import XlsxImportsPage from "./pages/XlsxImportsPage";
import RolesPage from "./pages/RolesPage";
import DatabaseBackupsPage from "./pages/DatabaseBackupsPage";
import LoginActivityPage from "./pages/LoginActivityPage";
import ReportsPage from "./pages/ReportsPage";
import ContractorOutputPage from "./pages/ContractorOutputPage";
import CityOutputPage from "./pages/CityOutputPage";
import NapUsagePage from "./pages/NapUsagePage";
import PdsAdsPage from "./pages/PdsAdsPage";
import DailyTLReportPage from "./pages/DailyTLReportPage";
import DailyTeamOutputPage from "./pages/DailyTeamOutputPage";
import DailyPersonnelOutputPage from "./pages/DailyPersonnelOutputPage";
import DailyMigrationTeamOutputPage from "./pages/DailyMigrationTeamOutputPage";
import DailyMigrationPersonnelOutputPage from "./pages/DailyMigrationPersonnelOutputPage";
import FSTDailyOutputPage from "./pages/FSTDailyOutputPage";
import ScorecardPage from "./pages/ScorecardPage";
import MigrationUploadedPage from "./pages/MigrationUploadedPage";
import MigrationDispatchPage from "./pages/MigrationDispatchPage";
import MigrationVisitedPage from "./pages/MigrationVisitedPage";
import MigrationCallingPage from "./pages/MigrationCallingPage";
import MigrationDashboardPage from "./pages/MigrationDashboardPage";
import MigrationDispatchDashboardPage from "./pages/MigrationDispatchDashboardPage";
import NIDashboardPage from "./pages/NIDashboardPage";
import TeamLeadersPage from "./pages/TeamLeadersPage";
import TeamsPage from "./pages/TeamsPage";
import CompaniesPage from "./pages/CompaniesPage";
import MessageCenterPage from "./pages/MessageCenterPage";
import PublicMigrationRequestPage from "./pages/PublicMigrationRequestPage";
import PublicMigrationRequestsPage from "./pages/PublicMigrationRequestsPage";
import NapLocatorPage from "./pages/NapLocatorPage";
import MobileDashboard from "./components/mobile/MobileDashboard";
import { ENABLE_JO_REGISTRY } from "./utils/featureFlags";
import { getFirstAllowedPage } from "./utils/pageAccess";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen text-gray-500">Loading…</div>;
  return user ? children : <Navigate to="/login" replace />;
}

function PermissionRoute({ children, permission }) {
  const { user, loading, hasPerm, permissions } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen text-gray-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!hasPerm(permission)) {
    const fallback = getFirstAllowedPage(permissions);
    if (!fallback) return <div className="flex items-center justify-center h-screen text-gray-500">You do not have access to any pages. Contact your administrator.</div>;
    return <Navigate to={fallback} replace />;
  }
  return children;
}

function XlsxRoute({ children }) {
  const { user, loading, canAccessXlsx, permissions } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen text-gray-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!canAccessXlsx) {
    const fallback = getFirstAllowedPage(permissions);
    if (!fallback) return <div className="flex items-center justify-center h-screen text-gray-500">You do not have access to any pages. Contact your administrator.</div>;
    return <Navigate to={fallback} replace />;
  }
  return children;
}

function AppRoutes() {
  const { user, loading, isMobileRole, canAccessNIDashboard, canAccessDashboard, hasPerm, permissions, mustChangePassword, logout, clearMustChangePassword } = useAuth();
  const isMobile = useIsMobile();
  const defaultPage = user ? (getFirstAllowedPage(permissions, { isMobile }) || null) : null;

  // Force password change — blocks ALL routes until password is changed
  if (!loading && user && mustChangePassword) {
    return (
      <ForceChangePasswordPage
        onSuccess={clearMustChangePassword}
        onLogout={async () => {
          await logout();
          window.location.href = "/login";
        }}
      />
    );
  }

  // Mobile-only roles (Sales Agent, FST, FST Migration, etc.): restricted to /m + migration pages
  if (!loading && user && isMobileRole) {
    // Pick the first page this user can actually access so /dashboard redirect never loops
    const mobileHome = canAccessNIDashboard
      ? "/m"
      : hasPerm("migration.view_uploaded") ? "/migration/uploaded"
      : hasPerm("migration.view_calling")  ? "/migration/for-calling"
      : hasPerm("migration.view_dispatch") ? "/migration/for-dispatch"
      : hasPerm("migration.view_visited")  ? "/migration/visited-called"
      : "/m";
    return (
      <Routes>
        <Route path="/login" element={<Navigate to={mobileHome} replace />} />
        <Route path="/m" element={canAccessNIDashboard ? <NIDashboardPage /> : <Navigate to={mobileHome} replace />} />
        {/* /dashboard is where PermissionRoute redirects on failure — send to role home */}
        <Route path="/dashboard" element={<Navigate to={mobileHome} replace />} />
        <Route path="/ni/dashboard" element={<PermissionRoute permission="page.ni_dashboard"><NIDashboardPage /></PermissionRoute>} />
        <Route path="/migration" element={<Navigate to={mobileHome} replace />} />
        <Route path="/migration/dashboard" element={<PermissionRoute permission="page.migration_dashboard"><MigrationDashboardPage /></PermissionRoute>} />
        <Route path="/migration/uploaded" element={<PermissionRoute permission="migration.view_uploaded"><MigrationUploadedPage /></PermissionRoute>} />
        <Route path="/migration/for-dispatch" element={<PermissionRoute permission="migration.view_dispatch"><MigrationDispatchPage /></PermissionRoute>} />
        <Route path="/migration/visited-called" element={<PermissionRoute permission="migration.view_visited"><MigrationVisitedPage /></PermissionRoute>} />
        <Route path="/migration/for-calling" element={<PermissionRoute permission="migration.view_calling"><MigrationCallingPage /></PermissionRoute>} />
        <Route path="/reports/fst-daily-output" element={<PermissionRoute permission="reports.fst_daily_output"><FSTDailyOutputPage /></PermissionRoute>} />
        <Route path="/nap-locator" element={<PermissionRoute permission="page.nap_locator"><NapLocatorPage /></PermissionRoute>} />
        <Route path="/messages" element={<PermissionRoute permission="chat.view"><MessageCenterPage /></PermissionRoute>} />
        {/* Block all other desktop routes — redirect to role home */}
        <Route path="*" element={<Navigate to={mobileHome} replace />} />
      </Routes>
    );
  }

  // Any role on a mobile device: mobile dashboard + admin pages with mobile navbar
  if (!loading && user && isMobile) {
    const mobileDefault = defaultPage || "/m";
    return (
      <Routes>
        <Route path="/login" element={<Navigate to={mobileDefault} replace />} />
        <Route path="/m" element={canAccessDashboard ? <MobileDashboard /> : <Navigate to={mobileDefault} replace />} />
        <Route path="/dashboard" element={<Navigate to={canAccessDashboard ? "/m" : mobileDefault} replace />} />
        <Route
          path="/ni/dashboard"
          element={<PermissionRoute permission="page.ni_dashboard"><NIDashboardPage /></PermissionRoute>}
        />
        <Route
          path="/manage-accounts"
          element={<PermissionRoute permission="page.manage_accounts"><ManageAccountsPage /></PermissionRoute>}
        />
        <Route
          path="/roles"
          element={<PermissionRoute permission="roles.view"><RolesPage /></PermissionRoute>}
        />
        {ENABLE_JO_REGISTRY && (
          <Route
            path="/jo-registry"
            element={<PermissionRoute permission="page.jo_registry"><JoRegistryPage /></PermissionRoute>}
          />
        )}
        <Route
          path="/xlsx-imports"
          element={<XlsxRoute><XlsxImportsPage /></XlsxRoute>}
        />
        <Route
          path="/database-backups"
          element={<PermissionRoute permission="page.database_backups"><DatabaseBackupsPage /></PermissionRoute>}
        />
        <Route
          path="/login-activity"
          element={<PermissionRoute permission="sessions.view"><LoginActivityPage /></PermissionRoute>}
        />
        <Route
          path="/team-leaders"
          element={<PermissionRoute permission="page.team_leaders"><TeamLeadersPage /></PermissionRoute>}
        />
        <Route
          path="/teams"
          element={<PermissionRoute permission="page.teams"><TeamsPage /></PermissionRoute>}
        />
        <Route
          path="/companies"
          element={<PermissionRoute permission="page.companies"><CompaniesPage /></PermissionRoute>}
        />
        <Route
          path="/reports"
          element={<PermissionRoute permission="page.reports"><ReportsPage /></PermissionRoute>}
        />
        <Route
          path="/reports/contractor-output"
          element={<PermissionRoute permission="reports.daily_contractor"><ContractorOutputPage /></PermissionRoute>}
        />
        <Route
          path="/reports/daily-tl-output"
          element={<PermissionRoute permission="reports.daily_tl"><DailyTLReportPage /></PermissionRoute>}
        />
        <Route
          path="/reports/daily-team-output"
          element={<PermissionRoute permission="reports.daily_team"><DailyTeamOutputPage /></PermissionRoute>}
        />
        <Route
          path="/reports/daily-personnel-output"
          element={<PermissionRoute permission="reports.daily_personnel"><DailyPersonnelOutputPage /></PermissionRoute>}
        />
        <Route
          path="/reports/daily-migration-team-output"
          element={<PermissionRoute permission="reports.daily_migration_team"><DailyMigrationTeamOutputPage /></PermissionRoute>}
        />
        <Route
          path="/reports/daily-migration-personnel-output"
          element={<PermissionRoute permission="reports.daily_migration_personnel"><DailyMigrationPersonnelOutputPage /></PermissionRoute>}
        />
        <Route
          path="/reports/city-output"
          element={<PermissionRoute permission="reports.output_municipality"><CityOutputPage /></PermissionRoute>}
        />
        <Route
          path="/reports/nap-usage"
          element={<PermissionRoute permission="reports.nap_usage"><NapUsagePage /></PermissionRoute>}
        />
        <Route
          path="/reports/fst-daily-output"
          element={<PermissionRoute permission="reports.fst_daily_output"><FSTDailyOutputPage /></PermissionRoute>}
        />
        <Route
          path="/reports/scorecard"
          element={<PermissionRoute permission="reports.scorecard"><ScorecardPage /></PermissionRoute>}
        />
        <Route
          path="/reports/pds-ads"
          element={<PermissionRoute permission="page.pds_ads"><PdsAdsPage /></PermissionRoute>}
        />
        <Route
          path="/migration"
          element={<Navigate to="/migration/uploaded" replace />}
        />
        <Route
          path="/migration/dashboard"
          element={<PermissionRoute permission="page.migration_dashboard"><MigrationDashboardPage /></PermissionRoute>}
        />
        <Route
          path="/migration/uploaded"
          element={<PermissionRoute permission="migration.view_uploaded"><MigrationUploadedPage /></PermissionRoute>}
        />
        <Route
          path="/migration/for-dispatch"
          element={<PermissionRoute permission="migration.view_dispatch"><MigrationDispatchPage /></PermissionRoute>}
        />
        <Route
          path="/migration/visited-called"
          element={<PermissionRoute permission="migration.view_visited"><MigrationVisitedPage /></PermissionRoute>}
        />
        <Route
          path="/migration/for-calling"
          element={<PermissionRoute permission="migration.view_calling"><MigrationCallingPage /></PermissionRoute>}
        />
        <Route
          path="/nap-locator"
          element={<PermissionRoute permission="page.nap_locator"><NapLocatorPage /></PermissionRoute>}
        />
        <Route
          path="/messages"
          element={<PermissionRoute permission="chat.view"><MessageCenterPage /></PermissionRoute>}
        />
        <Route path="*" element={<Navigate to={mobileDefault} replace />} />
      </Routes>
    );
  }

  const desktopDefault = defaultPage || "/dashboard";
  return (
    <Routes>
      <Route
        path="/login"
        element={loading ? null : user ? <Navigate to={desktopDefault} replace /> : <LoginPage />}
      />
      <Route
        path="/dashboard"
        element={<PermissionRoute permission="page.dashboard"><DashboardPage /></PermissionRoute>}
      />
      <Route
        path="/ni/dashboard"
        element={<PermissionRoute permission="page.ni_dashboard"><NIDashboardPage /></PermissionRoute>}
      />
      <Route
        path="/manage-accounts"
        element={<PermissionRoute permission="page.manage_accounts"><ManageAccountsPage /></PermissionRoute>}
      />
      <Route
        path="/roles"
        element={<PermissionRoute permission="roles.view"><RolesPage /></PermissionRoute>}
      />
      {ENABLE_JO_REGISTRY && (
        <Route
          path="/jo-registry"
          element={<PermissionRoute permission="page.jo_registry"><JoRegistryPage /></PermissionRoute>}
        />
      )}
      <Route
        path="/xlsx-imports"
        element={<XlsxRoute><XlsxImportsPage /></XlsxRoute>}
      />
      <Route
        path="/database-backups"
        element={<PermissionRoute permission="page.database_backups"><DatabaseBackupsPage /></PermissionRoute>}
      />
      <Route
        path="/login-activity"
        element={<PermissionRoute permission="sessions.view"><LoginActivityPage /></PermissionRoute>}
      />
      <Route
        path="/team-leaders"
        element={<PermissionRoute permission="page.team_leaders"><TeamLeadersPage /></PermissionRoute>}
      />
      <Route
        path="/teams"
        element={<PermissionRoute permission="page.teams"><TeamsPage /></PermissionRoute>}
      />
      <Route
        path="/companies"
        element={<PermissionRoute permission="page.companies"><CompaniesPage /></PermissionRoute>}
      />
      <Route
        path="/reports"
        element={<PermissionRoute permission="page.reports"><ReportsPage /></PermissionRoute>}
      />
      <Route
        path="/reports/contractor-output"
        element={<PermissionRoute permission="reports.daily_contractor"><ContractorOutputPage /></PermissionRoute>}
      />
      <Route
        path="/reports/daily-tl-output"
        element={<PermissionRoute permission="reports.daily_tl"><DailyTLReportPage /></PermissionRoute>}
      />
      <Route
        path="/reports/daily-team-output"
        element={<PermissionRoute permission="reports.daily_team"><DailyTeamOutputPage /></PermissionRoute>}
      />
      <Route
        path="/reports/daily-personnel-output"
        element={<PermissionRoute permission="reports.daily_personnel"><DailyPersonnelOutputPage /></PermissionRoute>}
      />
      <Route
        path="/reports/daily-migration-team-output"
        element={<PermissionRoute permission="reports.daily_migration_team"><DailyMigrationTeamOutputPage /></PermissionRoute>}
      />
      <Route
        path="/reports/daily-migration-personnel-output"
        element={<PermissionRoute permission="reports.daily_migration_personnel"><DailyMigrationPersonnelOutputPage /></PermissionRoute>}
      />
      <Route
        path="/reports/city-output"
        element={<PermissionRoute permission="reports.output_municipality"><CityOutputPage /></PermissionRoute>}
      />
      <Route
        path="/reports/nap-usage"
        element={<PermissionRoute permission="reports.nap_usage"><NapUsagePage /></PermissionRoute>}
      />
      <Route
        path="/reports/fst-daily-output"
        element={<PermissionRoute permission="reports.fst_daily_output"><FSTDailyOutputPage /></PermissionRoute>}
      />
      <Route
        path="/reports/scorecard"
        element={<PermissionRoute permission="reports.scorecard"><ScorecardPage /></PermissionRoute>}
      />
      <Route
        path="/reports/pds-ads"
        element={<PermissionRoute permission="page.pds_ads"><PdsAdsPage /></PermissionRoute>}
      />
      <Route
        path="/migration"
        element={<Navigate to="/migration/uploaded" replace />}
      />
      <Route
        path="/migration/dashboard"
        element={<PermissionRoute permission="page.migration_dashboard"><MigrationDashboardPage /></PermissionRoute>}
      />
      <Route
        path="/migration/dispatch-dashboard"
        element={<PermissionRoute permission="page.migration_dashboard"><MigrationDispatchDashboardPage /></PermissionRoute>}
      />
      <Route
        path="/migration/uploaded"
        element={<PermissionRoute permission="migration.view_uploaded"><MigrationUploadedPage /></PermissionRoute>}
      />
      <Route
        path="/migration/for-dispatch"
        element={<PermissionRoute permission="migration.view_dispatch"><MigrationDispatchPage /></PermissionRoute>}
      />
      <Route
        path="/migration/visited-called"
        element={<PermissionRoute permission="migration.view_visited"><MigrationVisitedPage /></PermissionRoute>}
      />
      <Route
        path="/migration/for-calling"
        element={<PermissionRoute permission="migration.view_calling"><MigrationCallingPage /></PermissionRoute>}
      />
      <Route
        path="/migration/public-requests"
        element={<PermissionRoute permission="page.public_migration_requests"><PublicMigrationRequestsPage /></PermissionRoute>}
      />
      <Route
        path="/nap-locator"
        element={<PermissionRoute permission="page.nap_locator"><NapLocatorPage /></PermissionRoute>}
      />
      <Route
        path="/messages"
        element={<PermissionRoute permission="chat.view"><MessageCenterPage /></PermissionRoute>}
      />
      <Route path="*" element={<Navigate to={desktopDefault} replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <Routes>
      {/* PUBLIC routes (no auth, no DMS layout). Must be matched BEFORE
          the authenticated app so /me is never called for public users. */}
      <Route path="/migration-request" element={<PublicMigrationRequestPage />} />

      {/* Authenticated DMS app */}
      <Route
        path="/*"
        element={
          <AuthProvider>
            <ConfirmProvider>
              <AppRoutes />
            </ConfirmProvider>
          </AuthProvider>
        }
      />
    </Routes>
  );
}
